import { useEffect, useState } from 'react';

const cache = new Map<string, string>();

/**
 * Extracts a square region of a PNG atlas via canvas, returning a data URL
 * usable directly as `border-image-source`. Cached by key (url + coords +
 * size) to avoid re-extracting on every mount.
 *
 * Returns `null` during the first load; the caller must handle the fallback
 * (style without border-image in the meantime).
 */
export function useNineSliceSource(
  atlasUrl: string,
  x: number,
  y: number,
  width: number,
  height: number = width,
): string | null {
  const key = `${atlasUrl}@${x},${y},${width}x${height}`;
  const [src, setSrc] = useState<string | null>(() => cache.get(key) ?? null);

  useEffect(() => {
    const cached = cache.get(key);
    if (cached) {
      setSrc(cached);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) {
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, x, y, width, height, 0, 0, width, height);
      const dataUrl = canvas.toDataURL();
      cache.set(key, dataUrl);
      setSrc(dataUrl);
    };
    img.src = atlasUrl;
    return () => {
      cancelled = true;
    };
  }, [key, atlasUrl, x, y, width, height]);

  return src;
}
